import { LitElement, html, nothing } from 'lit';
import { currentLang } from '../i18n';
import { formatDeadlineLabel, formatGoalDeadlineLabel, formatGoalEventLabel, goalDeadlineDate, isGoalDeadlineProvisional, isGoalOverdue, isOverdue, provisionalDeadlineCopy } from '../helpers';
import { renderSourceIcon, resolveSourceIcon } from '../source-icons';
import { sanitizeHttpUrl, sanitizeImageUrl } from '../url-sanitizer';
import { canonicalJudgmentEntries, hasCanonicalJudgmentPacket } from '../canonical-judgment';
import { canonicalJudgmentChosenOptionKey, hasGoalDodRows, isCanonicalJudgmentSelectionPending, isGoalRebuilding, pendingActorAnswerOptionKey } from '../goals-actions';
import { goalDodDisplayText } from '../goal-dod-display';
import {
  buildGoalTimeline,
  doneWorkGroupText,
  goalConditions,
  goalProgressCounts,
  goalSummaryLine,
  goalTurnLine,
  hasOpenJudgment,
  judgmentRefreshNote,
  timelineDateLabel,
  type TimelineEntry,
} from '../goal-detail-view';
import { goalSourceLink, isRuntimeTask } from '../work-detail-view';
import type { GoalDodReadinessLine, GoalWithSubtasks, SubtaskWithResult } from '../types';
import type { GoalDodRowsSaveDetail } from './goal-dod-rows-editor';
import type { WorkListAction } from './work-list';
import {
  goalReadNotices,
  goalWorkNotices,
  isDecisionVisibleOnUserSurface,
  reviewCloseProposal,
  releaseHoldEventId,
  reviewCloseProposalNote,
  reviewClosure,
  reviewClosureNote,
  reviewProposalTemplate,
} from './goal-card';
import { defineZenComponent } from './register';
import './actor-judgment-panel';
import './canonical-judgment-panel';
import './goal-dod-rows-editor';

/**
 * #2749: the goal beside the work list, laid out like the extension's goal
 * detail (#2748 board 7): what comes next, the completion conditions, the
 * deadline and source, then one timeline of what happened and what comes
 * next, and a box for telling Zenmetry what it read wrong.
 */
export class ZenGoalDetail extends LitElement {
  static properties = {
    goal: { attribute: false },
    editing: { state: true },
    changing: { state: true },
  };

  declare goal: GoalWithSubtasks | null;
  declare editing: 'title' | 'conditions' | null;
  /** #3222: the change being told in the bottom box, if one is open. */
  declare changing: 'asked' | 'replied' | 'tasks' | null;

  constructor() {
    super();
    this.goal = null;
    this.editing = null;
    this.changing = null;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.display = 'contents';
  }

  willUpdate(changed: Map<string, unknown>) {
    // Another goal starts without an open editor.
    const before = changed.get('goal') as GoalWithSubtasks | null | undefined;
    if (before && before.id !== this.goal?.id) {
      this.editing = null;
      this.changing = null;
    }
  }

  private get en() {
    return currentLang === 'en';
  }

  private emit(detail: WorkListAction) {
    this.dispatchEvent(new CustomEvent<WorkListAction>('work-list-action', { detail, bubbles: true, composed: true }));
  }

  /** The goal card's own events, which app.ts already answers. */
  private emitGoal(name: string, detail: Record<string, unknown>) {
    this.dispatchEvent(new CustomEvent(name, { detail: { goalId: this.goal?.id ?? '', ...detail }, bubbles: true, composed: true }));
  }

  private async startEditing(what: 'title' | 'conditions') {
    this.editing = what;
    await this.updateComplete;
    const field = this.querySelector<HTMLInputElement | HTMLTextAreaElement>('.goal-detail-title-input, .goal-detail-conditions-input');
    field?.focus();
    field?.select();
  }

  private saveTitle(input: HTMLInputElement) {
    if (this.editing !== 'title' || !this.goal) return;
    const title = input.value.trim();
    this.editing = null;
    if (title && title !== this.goal.title) this.emit({ action: 'goal-title', goalId: this.goal.id, title });
  }

  private saveConditions(textarea: HTMLTextAreaElement, current: string) {
    if (this.editing !== 'conditions' || !this.goal) return;
    const text = textarea.value.trim();
    this.editing = null;
    if (text !== current) this.emit({ action: 'goal-conditions', goalId: this.goal.id, text });
  }

  private judgmentEntries(goal: GoalWithSubtasks) {
    return canonicalJudgmentEntries(goal.simplified_action_intake);
  }

  /** Decisions without a canonical packet still read and answer here. */
  private openDecisions(goal: GoalWithSubtasks): SubtaskWithResult[] {
    if (hasCanonicalJudgmentPacket(goal.simplified_action_intake?.judgment_packet)) return [];
    return (goal.decisions || []).filter((st) => isDecisionVisibleOnUserSurface(st) && !(st.decision_result && st.status === 'done'));
  }

  private renderTitle(goal: GoalWithSubtasks) {
    if (this.editing === 'title') {
      return html`<input class="goal-detail-title-input" type="text" .value=${goal.title} aria-label=${this.en ? 'Goal name' : 'ゴールの名前'}
        @blur=${(e: Event) => this.saveTitle(e.target as HTMLInputElement)}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { e.stopPropagation(); this.editing = null; }
        }}>`;
    }
    const label = this.en ? 'Edit the goal name' : 'ゴールの名前を直す';
    return html`<h3 class=${`goal-detail-title${goal.status === 'done' ? ' done' : ''}`}>
      <button type="button" class="goal-detail-title-button" title=${label} @click=${() => this.startEditing('title')}>${goal.title}</button>
    </h3>`;
  }

  private renderConditions(goal: GoalWithSubtasks) {
    const label = this.en ? 'Done when' : '完了の条件';
    const edit = this.en ? 'Edit the conditions' : '条件を直す';
    const head = html`<div class="goal-detail-label"><span class="mi mi-xs" aria-hidden="true">task_alt</span>${label}
      ${this.editing === 'conditions' ? nothing : html`<button type="button" class="goal-detail-label-edit" title=${edit} aria-label=${edit} @click=${() => this.startEditing('conditions')}><span class="mi mi-xs" aria-hidden="true">edit</span></button>`}
    </div>`;
    if (this.editing === 'conditions') {
      if (hasGoalDodRows(goal)) {
        return html`<div class="goal-detail-section">${head}
          <zen-goal-dod-rows-editor .rows=${goal.dod_rows ?? []}
            @dod-rows-cancel=${() => { this.editing = null; }}
            @dod-rows-save=${(e: Event) => {
              const { edits } = (e as CustomEvent<GoalDodRowsSaveDetail>).detail;
              this.editing = null;
              if (edits.length > 0) this.emit({ action: 'goal-condition-rows', goalId: goal.id, edits });
            }}></zen-goal-dod-rows-editor>
        </div>`;
      }
      const current = goalDodDisplayText(goal.dod, goal.goal_dod_items);
      return html`<div class="goal-detail-section">${head}
        <textarea class="goal-detail-conditions-input" rows="3" .value=${current} aria-label=${label}
          @blur=${(e: Event) => this.saveConditions(e.target as HTMLTextAreaElement, current)}
          @keydown=${(e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); this.editing = null; } }}></textarea>
      </div>`;
    }
    const conditions = goalConditions(goal);
    const text = goalDodDisplayText(goal.dod, goal.goal_dod_items);
    const drift = goal.reconcile_status === 'drifted'
      ? html`<div class="goal-detail-drift"><span class="mi mi-xs" aria-hidden="true">rate_review</span>${this.en ? 'The tasks may no longer match these conditions.' : 'タスクがこの条件と合っていないかもしれません。'}</div>`
      : nothing;
    let body;
    if (conditions.length > 0) {
      body = html`<ul class="goal-detail-conditions">${conditions.map((item) => {
        const state = item.met ? (this.en ? 'Met' : '満たした') : (this.en ? 'Not yet' : 'まだ');
        return html`<li class=${`goal-detail-condition${item.met ? ' met' : ''}`}>
          <span class="mi mi-sm" role="img" aria-label=${state}>${item.met ? 'check_circle' : 'radio_button_unchecked'}</span><div class="goal-detail-condition-body"><span>${item.text}</span>${this.renderReadiness(item.readiness)}</div>
        </li>`;
      })}</ul>`;
    } else if (text) {
      body = html`<div class="goal-detail-conditions-text">${text}</div>`;
    } else {
      body = html`<button type="button" class="goal-detail-conditions-empty" @click=${() => this.startEditing('conditions')}>${this.en ? 'Add what "done" looks like' : '完了の条件を書く'}</button>`;
    }
    return html`<div class="goal-detail-section">${head}${body}${drift}</div>`;
  }

  /** #3560 stage 2: the lines saying where the meeting is held is ready, under its condition. */
  private renderReadiness(lines: GoalDodReadinessLine[]) {
    if (lines.length === 0) return nothing;
    const look = {
      done: { icon: 'check_circle', label: this.en ? 'Done' : '済み' },
      waiting: { icon: 'hourglass_top', label: this.en ? 'Waiting on them' : '相手の対応待ち' },
      open: { icon: 'radio_button_unchecked', label: this.en ? 'Not yet' : 'まだ' },
    };
    return html`<ul class="goal-detail-readiness">${lines.map((line) => {
      const state = look[line.state] ?? look.open;
      return html`<li class=${`goal-detail-readiness-line ${line.state}`}>
        <span class="mi mi-xs" role="img" aria-label=${state.label}>${state.icon}</span><span>${line.what}</span><span class="goal-detail-readiness-state">${state.label}</span>
      </li>`;
    })}</ul>`;
  }

  private renderMeta(goal: GoalWithSubtasks) {
    const parts: unknown[] = [];
    const deadlineISO = (goalDeadlineDate(goal) ?? '').slice(0, 10);
    const overdue = goal.status !== 'done' && isGoalOverdue(goal);
    const editDeadline = (e: Event) => this.emitGoal('goal-edit-deadline', { element: e.currentTarget, currentISO: deadlineISO });
    const provisional = goal.deadline && isGoalDeadlineProvisional(goal) ? provisionalDeadlineCopy() : null;
    parts.push(html`<button type="button" class=${`goal-detail-chip${overdue ? ' overdue' : ''}`} title=${provisional ? provisional.title : nothing} @click=${editDeadline}>
      <span class="mi mi-xs" aria-hidden="true">${overdue ? 'error' : 'event'}</span>${provisional ? html`<span class="deadline-provisional" aria-label=${provisional.aria}>${provisional.tag}</span>` : nothing}${goal.deadline ? formatGoalDeadlineLabel(goal) : (this.en ? 'No deadline' : '期限なし')}
    </button>`);
    // #2320: the event time sits beside the deadline, so a meeting's own time is not hidden behind its reply deadline.
    const eventLabel = formatGoalEventLabel(goal);
    if (eventLabel) {
      parts.push(html`<span class="goal-detail-chip" title=${this.en ? 'Event time' : '開催日時'}>
        <span class="mi mi-xs" aria-hidden="true">schedule</span>${eventLabel}
      </span>`);
    }
    // goal.md "Dates in a Goal" decision 13: only the first occurrence of a series whose rule was not read is a row.
    if (goal.dates?.series_rule_unread) {
      parts.push(html`<span class="goal-detail-chip goal-detail-chip-note">
        <span class="mi mi-xs" aria-hidden="true">warning</span>${this.en
          ? 'Could not read how this event repeats. Please check your calendar.'
          : '繰り返しの決まりを読めませんでした。カレンダーで確かめてください'}
      </span>`);
    }
    // #2282 (owner 2026-10-18): the tasks were made, and this says why the source was not read again.
    const refresh = goal.judgment_refresh_unavailable;
    if (refresh) {
      parts.push(html`<span class="goal-detail-chip goal-detail-chip-note">
        <span class="mi mi-xs" aria-hidden="true">warning</span>${judgmentRefreshNote(refresh, this.en)}
      </span>`);
    }
    const source = goalSourceLink(goal);
    const url = sanitizeHttpUrl(source.url);
    if (url) {
      const icon = resolveSourceIcon(url, source.kind);
      const label = icon.kind === 'web' || icon.kind === 'generic' ? (this.en ? 'Source' : '元の情報') : icon.label;
      parts.push(html`<a class="goal-detail-chip" href=${url} target="_blank" rel="noopener noreferrer">${renderSourceIcon(url, source.kind, 'xs')}${label}</a>`);
    }
    parts.push(html`<button type="button" class="goal-detail-chip goal-detail-ask-ai" @click=${() => this.emitGoal('goal-ask-ai', { title: goal.title })}>
      <span class="mi mi-xs" aria-hidden="true">smart_toy</span>${this.en ? 'Ask AI' : 'AIに相談'}
    </button>`);
    return html`<div class="goal-detail-meta">${parts}</div>`;
  }

  private renderActor(entry: TimelineEntry) {
    const config = {
      you: { icon: 'person', label: this.en ? 'You' : 'あなた' },
      ai: { icon: 'smart_toy', label: 'AI' },
      other: { icon: 'person_outline', label: entry.actorName || (this.en ? 'Them' : '相手') },
      event: { icon: 'event', label: this.en ? 'Event' : '予定' },
    }[entry.actor];
    const inner = html`<span class="mi mi-xs" aria-hidden="true">${config.icon}</span>${config.label}`;
    const st = entry.kind === 'task' ? entry.subtask : null;
    // An open task's badge switches who does it.
    if (st && st.status === 'pending' && !isRuntimeTask(st)) {
      const ai = st.executor === 'ai';
      const action = ai ? (this.en ? 'Do it myself' : '自分でやる') : (this.en ? 'Hand to AI' : 'AIに任せる');
      return html`<button type="button" class=${`goal-timeline-actor goal-timeline-actor-${entry.actor} goal-timeline-actor-switch`} title=${action} aria-label=${`${config.label}: ${action}`}
        @click=${() => this.emit({ action: 'executor', subtaskId: st.id, executor: ai ? 'human' : 'ai' })}>${inner}</button>`;
    }
    return html`<span class=${`goal-timeline-actor goal-timeline-actor-${entry.actor}`}>${inner}</span>`;
  }

  private renderTask(goal: GoalWithSubtasks, st: SubtaskWithResult) {
    const done = st.status === 'done';
    const runtime = isRuntimeTask(st);
    const en = this.en;
    const checkLabel = done ? (en ? 'Mark task not done' : '未完了に戻す') : (en ? 'Mark task done' : 'タスクを完了にする');
    const status = st.status === 'review'
      ? html`<span class="goal-timeline-status"><span class="mi mi-xs" aria-hidden="true">rate_review</span>${en ? 'For your review' : 'ご確認待ち'}</span>`
      : st.status === 'in_progress'
        ? html`<span class="goal-timeline-status"><span class="mi mi-xs" aria-hidden="true">progress_activity</span>${en ? 'AI is working' : 'AIが対応中'}</span>`
        : nothing;
    const closure = done ? reviewClosure(st) : null;
    const proposal = done ? null : reviewCloseProposal(st);
    const releaseEventId = proposal ? releaseHoldEventId(st) : null;
    return html`<div class=${`goal-timeline-task${done ? ' done' : ''}`}>
      <button type="button" class=${`goal-timeline-check${done ? ' checked' : ''}`} aria-label=${checkLabel} title=${checkLabel} ?disabled=${runtime}
        @click=${() => this.emit(done ? { action: 'undo-step', subtaskId: st.id } : { action: 'check-step', subtaskId: st.id })}>
        ${done ? html`<span class="mi mi-xs" aria-hidden="true">check</span>` : nothing}
      </button>
      <div class="goal-timeline-task-main">
        <button type="button" class="goal-timeline-task-title" @click=${() => this.emit({ action: 'open-task', goalId: goal.id, subtaskId: st.id })}>${st.title}</button>
        ${status !== nothing ? html`<div class="goal-timeline-task-meta">${status}</div>` : nothing}
        ${closure ? html`<div class="goal-timeline-note">${reviewClosureNote(closure)}
          <button type="button" class="goal-timeline-note-button" @click=${() => this.emit({ action: 'undo-step', subtaskId: st.id })}>${en ? 'Undo' : '戻す'}</button></div>` : nothing}
        ${proposal && releaseEventId ? html`<div class="goal-timeline-note">${reviewCloseProposalNote(proposal)}
          <button type="button" class="goal-timeline-note-button" @click=${() => this.emit({ action: 'release-hold', goalId: goal.id, eventId: releaseEventId, subtaskId: st.id })}>${en ? 'Remove the hold' : '外す'}</button>
          <button type="button" class="goal-timeline-note-button" @click=${() => this.emit({ action: 'close-proposal', subtaskId: st.id, accept: true })}>${en ? 'Keep the hold' : '残す'}</button></div>`
          : proposal ? html`<div class="goal-timeline-note">${reviewCloseProposalNote(proposal)}
          <button type="button" class="goal-timeline-note-button" @click=${() => this.emit({ action: 'close-proposal', subtaskId: st.id, accept: true })}>${en ? (proposal.reason === 'done' ? 'Mark done' : 'Mark not needed') : (proposal.reason === 'done' ? '完了にする' : '不要にする')}</button>
          <button type="button" class="goal-timeline-note-button" @click=${() => this.emit({ action: 'close-proposal', subtaskId: st.id, accept: false })}>${en ? 'Keep open' : 'まだ残す'}</button></div>` : nothing}
      </div>
    </div>`;
  }

  private renderEntryBody(goal: GoalWithSubtasks, entry: TimelineEntry) {
    if (entry.kind === 'task' && entry.group && entry.groupKind) {
      // Owner 2026-10-28: done work of one kind on one day is one line.
      const label = this.en ? 'Done' : '完了';
      return html`<div class="goal-timeline-task done">
        <span class="goal-timeline-check checked" role="img" aria-label=${label} title=${label}><span class="mi mi-xs" aria-hidden="true">check</span></span>
        <div class="goal-timeline-task-main"><span class="goal-timeline-task-title">${doneWorkGroupText(entry.groupKind, entry.group, this.en ? 'en' : 'ja')}</span></div>
      </div>`;
    }
    if (entry.kind === 'task' && entry.subtask) return this.renderTask(goal, entry.subtask);
    if (entry.kind === 'event') {
      const date = entry.at ? new Date(entry.at) : null;
      const time = date && goal.linked_event?.is_all_day !== true
        ? `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')} `
        : '';
      return html`<div class="goal-timeline-text">${time}${entry.text}</div>`;
    }
    const icon = entry.kind === 'waiting'
      ? html`<span class="mi mi-xs" role="img" aria-label=${this.en ? 'Waiting' : '対応待ち'}>hourglass_top</span>`
      : nothing;
    return html`<div class="goal-timeline-text">${icon}${entry.text}${entry.what ? `｜${entry.what}` : ''}</div>`;
  }

  /**
   * The date column. An open task's own deadline is shown once, here, and
   * opens the date picker; a task that keeps the goal's deadline shows no date
   * (the goal's chip has it) (#2857).
   */
  private renderDate(entry: TimelineEntry, now: Date) {
    const label = timelineDateLabel(entry, now, this.en ? 'en' : 'ja');
    const st = entry.kind === 'task' ? entry.subtask : null;
    if (!st || st.status === 'done' || entry.inheritsDeadline || !entry.at) return html`<span class="goal-timeline-date">${label}</span>`;
    const overdue = isOverdue(entry.at);
    const inner = html`${overdue ? html`<span class="mi mi-xs" role="img" aria-label=${this.en ? 'Overdue' : '期限切れ'}>error</span>` : nothing}${label}`;
    if (isRuntimeTask(st)) return html`<span class=${`goal-timeline-date${overdue ? ' overdue' : ''}`}>${inner}</span>`;
    const edit = this.en ? 'Change the deadline' : '期限を変える';
    return html`<button type="button" class=${`goal-timeline-date goal-timeline-date-edit${overdue ? ' overdue' : ''}`} title=${edit} aria-label=${`${edit}: ${formatDeadlineLabel(entry.at)}`}
      @click=${(e: Event) => this.emit({ action: 'task-deadline', subtaskId: st.id, element: e.currentTarget as HTMLElement, current: entry.at!.slice(0, 10) })}>${inner}</button>`;
  }

  private renderRow(goal: GoalWithSubtasks, entry: TimelineEntry, now: Date, past: boolean) {
    return html`<li class=${`goal-timeline-row${past ? ' past' : ''}`}>
      ${this.renderDate(entry, now)}
      <div class="goal-timeline-body">${this.renderActor(entry)}${this.renderEntryBody(goal, entry)}</div>
    </li>`;
  }

  private renderDecision(st: SubtaskWithResult) {
    return html`<div class="goal-detail-decision">
      <div class="goal-detail-decision-title">${st.title}</div>
      ${(st.decision_materials || []).map((m) => html`<div class="goal-detail-decision-material"><span>${m.title}:</span> ${m.content}</div>`)}
      <div class="goal-detail-decision-options">
        ${(st.decision_options || []).map((opt) => html`<button type="button" class=${`task-detail-chip${opt.recommended ? ' recommended' : ''}`}
          @click=${() => this.emitGoal('subtask-make-decision', { subtaskId: st.id, value: opt.value || opt.label, label: opt.label })}>${opt.label}</button>`)}
      </div>
    </div>`;
  }

  private renderJudgments(goal: GoalWithSubtasks) {
    const entries = this.judgmentEntries(goal);
    const decisions = this.openDecisions(goal);
    const proposals = goal.simplified_action_intake?.review_change_proposals ?? [];
    // #2732: 「誰が進めますか」 is a judgment like the others, answered or not.
    const actorQuestions = goal.actor_questions;
    const actorConditions = actorQuestions?.conditions ?? [];
    if (entries.length === 0 && decisions.length === 0 && proposals.length === 0 && actorConditions.length === 0) return null;
    return html`
      ${actorConditions.map((condition) => html`<zen-actor-judgment-panel
        .goalId=${goal.id}
        .conditionRef=${condition.condition_ref}
        .label=${condition.label}
        .answer=${condition.answer ?? null}
        .teammates=${actorQuestions?.teammates ?? []}
        .pendingOptionKey=${pendingActorAnswerOptionKey(goal.id, condition.condition_ref)}
      ></zen-actor-judgment-panel>`)}
      ${entries.map((entry) => html`<zen-canonical-judgment-panel
        .goalId=${goal.id}
        .packet=${entry.packet}
        .conditionRef=${entry.condition_ref}
        .axis=${entry.axis}
        .pending=${isCanonicalJudgmentSelectionPending(goal.id)}
        .chosenOptionKey=${canonicalJudgmentChosenOptionKey(goal.id, entry.packet?.selection_snapshot_token)}
      ></zen-canonical-judgment-panel>`)}
      ${decisions.map((st) => this.renderDecision(st))}
      ${proposals.map((item) => reviewProposalTemplate(goal.id, item, this))}
    `;
  }

  private renderTimeline(goal: GoalWithSubtasks, judgments: unknown, openJudgment: boolean, now: Date) {
    const timeline = buildGoalTimeline(goal, now);
    const { past, upcoming } = timeline;
    if (past.length === 0 && upcoming.length === 0 && !judgments) return nothing;
    const en = this.en;
    const counts = goalProgressCounts(timeline, openJudgment);
    const count = (key: string, label: string, n: number) => html`<span class=${`goal-detail-count goal-detail-count-${key}`}>${label}<b>${n}</b></span>`;
    return html`<div class="goal-detail-section">
      <div class="goal-detail-label"><span class="mi mi-xs" aria-hidden="true">timeline</span>${en ? 'Progress' : '進捗状況'}
        <span class="goal-detail-counts">
          ${count('active', en ? 'In progress' : '進行中', counts.active)}${count('waiting', en ? 'Waiting' : '対応待ち', counts.waiting)}${count('done', en ? 'Done' : '完了', counts.done)}
        </span>
      </div>
      <ol class="goal-timeline">
        ${past.map((entry) => this.renderRow(goal, entry, now, true))}
        ${past.length > 0 ? html`<li class="goal-timeline-now" aria-label=${en ? 'Next steps start here' : 'ここから先がこれから'}><span class="goal-timeline-date">${en ? 'Now' : '今'}</span><span class="goal-timeline-now-line"></span></li>` : nothing}
        ${judgments ? html`<li class="goal-timeline-row judgment"><span class="goal-timeline-date">${en ? 'Today' : '今日'}</span>
          <div class="goal-timeline-body"><span class="goal-timeline-actor goal-timeline-actor-you"><span class="mi mi-xs" aria-hidden="true">alt_route</span>${en ? 'Your call' : 'ご判断'}</span>${judgments}</div></li>` : nothing}
        ${goal.status !== 'done' && isGoalRebuilding(goal) ? html`<li class="goal-timeline-row goal-timeline-rebuilding" role="status">
          <span class="goal-timeline-date"></span>
          <div class="goal-timeline-body"><span class="mi mi-sm" aria-hidden="true">progress_activity</span>${en ? 'Updating the tasks to match…' : '作業を組み直しています…'}</div></li>` : nothing}
        ${upcoming.map((entry) => this.renderRow(goal, entry, now, false))}
      </ol>
    </div>`;
  }

  private renderSummary(goal: GoalWithSubtasks, hasJudgment: boolean, now: Date) {
    if (goal.status === 'done') return nothing;
    const line = goalSummaryLine(goal, hasJudgment, this.en ? 'en' : 'ja', now);
    return line ? html`<div class="goal-detail-summary">${line}</div>` : nothing;
  }

  /**
   * #3222: whose turn it is, under the goal's name. Shown only; what changes
   * it is in the box at the bottom (board 案D).
   */
  private renderTurn(goal: GoalWithSubtasks, hasJudgment: boolean) {
    const turn = goalTurnLine(goal, this.en ? 'en' : 'ja', hasJudgment);
    if (!turn) return nothing;
    return html`<div class="goal-detail-turn">
      <span class=${`goal-timeline-actor${turn.theirs || turn.ai ? '' : ' goal-timeline-actor-you'}`}>
        <span class="mi mi-xs" aria-hidden="true">${turn.ai ? 'smart_toy' : turn.theirs ? 'person_outline' : 'person'}</span>${turn.who}</span>
      <span class="goal-detail-turn-text">${turn.text}</span>
    </div>`;
  }

  /** 済んだことがある: the open tasks, each with a check. */
  private renderOpenTasks(goal: GoalWithSubtasks) {
    const en = this.en;
    const open = buildGoalTimeline(goal, new Date()).upcoming
      .map((entry) => entry.kind === 'task' ? entry.subtask : null)
      .filter((st): st is SubtaskWithResult => !!st && st.status !== 'done' && !isRuntimeTask(st));
    if (open.length === 0) return html`<div class="goal-detail-change-note">${en ? 'No open tasks.' : 'まだのタスクはありません。'}</div>`;
    return html`<ul class="goal-detail-change-tasks">
      ${open.map((st) => html`<li><button type="button" class="goal-timeline-check" aria-label=${en ? `Mark "${st.title}" done` : `「${st.title}」を完了にする`}
        @click=${() => this.emit({ action: 'check-step', subtaskId: st.id })}></button><span>${st.title}</span></li>`)}
    </ul>`;
  }

  /**
   * #3222 (board 案D): everything that changes the goal's state, in one place.
   * The chips follow whose turn it is; a turn change is sent with a note, so
   * the review rebuilds the next tasks from it.
   */
  private renderFeedback(goal: GoalWithSubtasks) {
    if (goal.status === 'done') return nothing;
    const en = this.en;
    const theirs = goalTurnLine(goal, en ? 'en' : 'ja')?.theirs ?? false;
    const changing = this.changing;
    const deadlineISO = (goalDeadlineDate(goal) ?? '').slice(0, 10);
    const chip = (icon: string, label: string, onClick: (event: Event) => void, on = false) =>
      html`<button type="button" class=${`task-detail-chip${on ? ' selected' : ''}`} aria-pressed=${on ? 'true' : 'false'} @click=${onClick}><span class="mi mi-xs" aria-hidden="true">${icon}</span>${label}</button>`;
    const toggle = (what: 'asked' | 'replied' | 'tasks') => async () => {
      this.changing = this.changing === what ? null : what;
      await this.updateComplete;
      if (this.changing === 'asked' || this.changing === 'replied') this.querySelector<HTMLTextAreaElement>('.goal-detail-feedback-input')?.focus();
    };
    const placeholder = changing === 'asked'
      ? (en ? 'Who did you ask, and for what? (e.g. asked Ms. Matsukura for the figures)' : '誰に、何を頼みましたか？（例：松倉さんに数字の確認を頼んだ）')
      : changing === 'replied'
        ? (en ? 'What did they say?' : 'どんな返事でしたか？')
        : (en ? 'Anything else? (e.g. Mr. Tanaka handles this now)' : 'ほかにあればどうぞ（例：担当は田中さんに変わった）');
    const send = en ? 'Send' : '伝える';
    const submit = () => {
      const input = this.querySelector('.goal-detail-feedback-input') as HTMLTextAreaElement | null;
      if (!input) return;
      const text = input.value.trim();
      if (changing === 'asked' || changing === 'replied') {
        const head = changing === 'asked' ? (en ? 'Asked the other side' : '相手に頼んだ') : (en ? 'They replied' : '返事が来た');
        this.emit({ action: 'goal-turn-change', goalId: goal.id, turn: changing === 'asked' ? 'counterparty' : 'principal', text: text ? `${head}${en ? ': ' : '：'}${text}` : head });
        this.changing = null;
      } else if (text) {
        this.emit({ action: 'goal-feedback', goalId: goal.id, text });
      } else {
        input.focus();
        return;
      }
      input.value = '';
    };
    const takeBack = () => {
      this.changing = null;
      this.emit({ action: 'goal-turn-change', goalId: goal.id, turn: 'principal', text: en ? 'I will move this forward myself' : '自分で進めることにした' });
    };
    return html`<div class="task-detail-feedback">
      <div class="task-detail-feedback-lead">${en ? 'Has anything changed?' : '何か状況に変更はありましたか？'}</div>
      <div class="task-detail-chips">
        ${theirs
          ? html`${chip('mark_email_read', en ? 'They replied' : '返事が来た', toggle('replied'), changing === 'replied')}
            ${chip('undo', en ? "I'll do it myself" : '自分で進める', takeBack)}`
          : chip('forward_to_inbox', en ? 'I asked someone' : '相手に頼んだ', toggle('asked'), changing === 'asked')}
        ${chip('task_alt', en ? 'Something is done' : '済んだことがある', toggle('tasks'), changing === 'tasks')}
        ${chip('event_busy', en ? 'Wrong deadline' : '期限が違う', (e) => this.emitGoal('goal-edit-deadline', { element: e.currentTarget, currentISO: deadlineISO }))}
        ${chip('edit', en ? 'Wrong conditions' : '条件が違う', () => this.startEditing('conditions'))}
        ${chip('block', en ? 'Not needed' : 'この件は不要', (e) => this.emitGoal('goal-delete', { element: e.currentTarget, title: goal.title }))}
      </div>
      ${changing === 'tasks' ? this.renderOpenTasks(goal) : nothing}
      <div class="task-detail-box">
        <textarea class="task-detail-input goal-detail-feedback-input" rows="2" placeholder=${placeholder} aria-label=${placeholder}></textarea>
        <button type="button" class="task-detail-send" aria-label=${send} title=${send} @click=${submit}><span class="mi mi-sm" aria-hidden="true">arrow_upward</span></button>
      </div>
    </div>`;
  }

  private renderAddTask(goal: GoalWithSubtasks) {
    if (goal.status === 'done') return nothing;
    const label = this.en ? 'Add a task' : 'タスクを追加';
    return html`<input class="goal-detail-add" type="text" placeholder=${label} aria-label=${label}
      @keydown=${(e: KeyboardEvent) => {
        if (e.key !== 'Enter' || e.isComposing) return;
        e.preventDefault();
        this.emitGoal('goal-add-subtask', { element: e.currentTarget });
      }}>`;
  }

  render() {
    const goal = this.goal;
    if (!goal) return nothing;
    const now = new Date();
    const judgments = goal.status === 'done' ? null : this.renderJudgments(goal);
    // An answered judgment stays shown as a record; only an open one asks the user.
    const openJudgment = !!judgments && (hasOpenJudgment(goal) || this.openDecisions(goal).length > 0);
    const screenshot = sanitizeImageUrl(goal.screenshot_url);
    const retry = (e: Event) => this.emitGoal('goal-retry-subtask-generation', { element: e.currentTarget });
    return html`
      <div class="goal-detail-head">
        ${this.renderTitle(goal)}
        ${this.renderTurn(goal, openJudgment)}
        ${this.renderSummary(goal, openJudgment, now)}
      </div>
      ${this.renderConditions(goal)}
      ${this.renderMeta(goal)}
      ${goalReadNotices(goal)}
      ${goalWorkNotices(goal, retry)}
      ${this.renderTimeline(goal, judgments, openJudgment, now)}
      ${screenshot ? html`<div class="goal-detail-section"><div class="goal-detail-label">${this.en ? 'Captured screenshot' : '取り込んだスクリーンショット'}</div>
        <img class="goal-detail-screenshot" src=${screenshot} alt=${this.en ? 'Captured screenshot' : '取り込んだスクリーンショット'} data-lightbox-src=${screenshot}></div>` : nothing}
      ${this.renderFeedback(goal)}
      ${this.renderAddTask(goal)}
    `;
  }
}

defineZenComponent('zen-goal-detail', ZenGoalDetail);
